import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import Landing from "./tabs/landing";
import { useUtilityServices } from "../../../readCycleManagement/hooks";
import {
  useAddRoute,
  useGetCategory,
  useGetPremises,
  useGetRouteData,
  useGetSubCategory,
  useUpdateRoute,
} from "../../hooks";
import {
  useGetResourceTypeList,
  useGetResourceSubtypeList,
} from "@features/mx/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import { PageHeader } from "@shared/components/PageHeader";

export function AddRoute() {
  const { action, route_id } = useParams();
  const isEditMode = action === "edit" && route_id;
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subCodeList, setSubCodeList] = useState<any>("");
  const [selectedResourceType, setSelectedResourceType] = useState<string[]>(
    [],
  );
  const [selectedPremises, setSelectedPremises] = useState<string[]>([]);
  const remoteUtilityId = useRemoteUtilityId() || "";
  const addRoute = useAddRoute();
  const updateRoute = useUpdateRoute(route_id);
  const { data: routeDetails, isLoading: isLoadingRouteDetails } =
    useGetRouteData(isEditMode ? route_id : undefined);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Fetch data using hooks
  const { data: utilities } = useUtilityServices();
  const { data: categoryData } = useGetCategory();
  const { data: subCategoryData } = useGetSubCategory(subCodeList);
  const { data: premisesData } = useGetPremises();

  // Resource Type & Sub Type hooks from shared mx hooks
  const { data: resourceTypeResponse } =
    useGetResourceTypeList(remoteUtilityId);
  const { data: subtypesResponse } = useGetResourceSubtypeList(
    remoteUtilityId,
    selectedResourceType && selectedResourceType.length > 0
      ? selectedResourceType
      : undefined,
  );

  // Prepare options
  const getUtilityOptions = utilities
    ? utilities
        .filter((utility) => utility.isActive === true)
        .map((utility) => ({
          value: String(utility.name),
          label: utility.name,
        }))
    : [];

  const categoryOptions =
    categoryData?.map((category) => ({
      value: String(category.code),
      label: category.name,
    })) || [];

  const subCategoryOptions =
    subCategoryData?.map((subCategory) => ({
      value: String(subCategory.code),
      label: subCategory.name,
    })) || [];

  const resourceTypeOptions = useMemo(() => {
    const list = resourceTypeResponse?.result || resourceTypeResponse || [];
    return Array.isArray(list)
      ? list.map((item: any) => ({
          label: item.name,
          value: item.code || item.id,
        }))
      : [];
  }, [resourceTypeResponse]);

  const resourceSubtypeOptions = useMemo(() => {
    const list = subtypesResponse?.result || subtypesResponse || [];
    return Array.isArray(list)
      ? list.map((item: any) => ({
          label: item.name,
          value: item.code || item.id,
        }))
      : [];
  }, [subtypesResponse]);

  const handleFieldChange = (fieldName: string, value: any) => {
    if (fieldName === "consumerCategory") {
      setSubCodeList(value);
      if (!value || (Array.isArray(value) && value.length === 0)) {
        form.setValue("consumerSubcategory", []);
      }
    }
    if (fieldName === "resourceType") {
      const types = Array.isArray(value) ? value : value ? [value] : [];
      setSelectedResourceType(types);
      form.setValue("resourceSubType", []);
    }
  };

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "routeName",
        label: "Route Name",
        type: "text",
        required: true,
        maxLength: 100,
        placeholder: "Enter route name",
        helperText: "Enter the name of the route",
        tooltipText:
          "A unique identifier for this route. Use descriptive names like 'Downtown-Water-Jan' for easy recognition.",
        showHelperTooltip: true,
      },
      {
        name: "utilityService",
        label: "Utility Service",
        type: "multi-select",
        required: true,
        placeholder: "Select utility service",
        options: getUtilityOptions,
        helperText: "Choose the utility services for this route",
        tooltipText:
          "Select one or more utility types this route will cover. This filters available meters and premises.",
        showHelperTooltip: true,
      },
      {
        name: "consumerCategory",
        label: "Consumer Category",
        type: "multi-select",
        required: true,
        options: categoryOptions,
        placeholder: "Select consumer category",
        helperText: "Choose a consumer category for the service order",
        tooltipText:
          "Primary consumer segment (Residential or Commercial). Determines available subcategories and billing rules.",
        showHelperTooltip: true,
      },
      {
        name: "consumerSubcategory",
        label: "Consumer Subcategory",
        type: "multi-select",
        required: true,
        options: subCategoryOptions,
        disabled:
          !subCodeList ||
          (Array.isArray(subCodeList) && subCodeList.length === 0),
        placeholder: "Select consumer subcategory",
        helperText: "Choose a consumer subcategory for the service order",
        className: "col-span-1",
        tooltipText:
          "Detailed consumer classification within the category. Used for targeted service planning and rate application.",
        showHelperTooltip: true,
      },
      {
        name: "resourceType",
        label: "Resource Type",
        type: "multi-select",
        required: true,
        placeholder: "Select resource type",
        options: resourceTypeOptions,
        helperText: "Choose the type of resource for this route",
        tooltipText: "Choose the type of resource for this route",
        showHelperTooltip: true,
      },
      {
        name: "resourceSubType",
        label: "Resource Sub Type",
        type: "multi-select",
        required: true,
        disabled: !selectedResourceType || selectedResourceType.length === 0,
        placeholder: "Select resource sub type",
        options: resourceSubtypeOptions,
        helperText: "Choose the sub type for the selected resource type",
        tooltipText: "Choose the sub type for the selected resource type",
        showHelperTooltip: true,
      },
    ],
    [
      getUtilityOptions,
      categoryOptions,
      subCategoryOptions,
      subCodeList,
      resourceTypeOptions,
      resourceSubtypeOptions,
      selectedResourceType,
    ],
  );

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: {
      routeName: "",
      utilityService: [],
      consumerCategory: [],
      consumerSubcategory: [],
      resourceType: [],
      resourceSubType: [],
    },
  });

  const watchedResourceType = form.watch("resourceType");
  useEffect(() => {
    const types = Array.isArray(watchedResourceType)
      ? watchedResourceType
      : watchedResourceType
        ? [watchedResourceType]
        : [];
    setSelectedResourceType(types);
  }, [watchedResourceType]);

  useEffect(() => {
    if (isEditMode && routeDetails && !isDataLoaded) {
      const routeData = routeDetails;

      form.setValue("routeName", String(routeData.routeName || ""));
      form.setValue(
        "utilityService",
        Array.isArray(routeData.utilityService)
          ? routeData.utilityService.map(String)
          : [],
      );
      form.setValue("consumerCategory", routeData.category || []);
      form.setValue("consumerSubcategory", routeData.subCategory || []);

      form.setValue("resourceType", routeData.resourceTypeCodes);
      form.setValue("resourceSubType", routeData.resourceSubTypeCodes);
      setSelectedResourceType(routeData.resourceTypeCodes);

      setSubCodeList(routeData.category?.[0] || "");
      setSelectedPremises(
        Array.isArray(routeData.premise) ? routeData.premise.map(String) : [],
      );

      setIsDataLoaded(true);
    }
  }, [isEditMode, routeDetails, isDataLoaded, form]);

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
    }, 1000);
  };

  const handleCancel = () => {
    form.reset();
    setSubCodeList("");
    setSelectedResourceType([]);
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);

    // ✅ Validate the form before proceeding
    const isValid = await FormService.validateForm(form);
    if (!isValid) {
      toast({
        title: "Please fill in all required fields.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    // ✅ Ensure at least one premise is selected
    if (!selectedPremises || selectedPremises.length === 0) {
      toast({
        title: "Please select at least one premise.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    const formValues = form.getValues();

    const payload = {
      route_name: formValues.routeName,
      utility_service: formValues.utilityService,
      category: formValues.consumerCategory,
      sub_category: formValues.consumerSubcategory,
      resource_type_codes: formValues.resourceType,
      resource_sub_type_codes: formValues.resourceSubType,
      premise: selectedPremises,
      remote_utility_id: remoteUtilityId,
    };

    try {
      switch (action) {
        case "add":
          await addRoute.mutateAsync(payload);
          break;
        case "edit":
          await updateRoute.mutateAsync(payload);
          break;
      }

      toast({
        title: `Route ${action === "edit" ? "updated" : "added"} successfully.`,
      });

      navigate("/mx/routeManagement/");
    } catch (error: any) {
      toast({
        title: error?.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="mb-4 flex items-center gap-4">
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700"
          onClick={() => navigate("/mx/routeManagement/")}
        />
        <div className="flex flex-col">
          <PageHeader
            title={`${action === "edit" ? "Edit" : "Create"} Route`}
            subtitle={`${action === "edit" ? "Edit" : "Create"} a new route`}
          />
        </div>
      </div>

      {/* Form Section */}
      <DynamicForm
        fields={formFields}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleCancel}
        onFieldChange={handleFieldChange}
        gridColumns={2}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          classes: FIELD_CLASSES,
        }}
      />

      {/* Landing Section */}
      <Landing
        selectedPremises={selectedPremises}
        setSelectedPremises={setSelectedPremises}
        resourceTypeCode={form.watch("resourceType")}
        resourceSubTypeCode={form.watch("resourceSubType")}
        category={form.watch("consumerCategory")}
        subCategory={form.watch("consumerSubcategory")}
      />

      {/* ✅ Final Submit Button */}
      <div className="flex justify-end pt-4 gap-3">
        <Button
          variant="outline"
          onClick={() => {
            handleCancel();
            navigate("/mx/routeManagement/");
          }}
        >
          Cancel
        </Button>

        <Button
          variant="default"
          className="bg-blue-600 hover:bg-blue-700"
          onClick={handleFinalSubmit}
          disabled={isSubmitting}
        >
          Submit
        </Button>
      </div>
    </div>
  );
}
export default AddRoute;
